/**
 * DeleteWithAuthDialog
 *
 * Diálogo de confirmación de eliminación con re-autenticación.
 * Muestra los recursos dependientes que serán eliminados en cascada
 * y solicita la contraseña del usuario activo antes de proceder.
 *
 * Uso:
 *   <DeleteWithAuthDialog
 *     isOpen={isDeleteOpen}
 *     onClose={() => setIsDeleteOpen(false)}
 *     onConfirm={handleDeleteConfirm}        // (password: string) => Promise<void>
 *     resourceType="empresa"
 *     resourceName={company.name}
 *     cascadeItems={[
 *       { label: "Sucursales", count: 3, icon: "branch" },
 *       { label: "Dueños", count: 1, icon: "user" },
 *       ...
 *     ]}
 *     isDeleting={isDeleting}
 *   />
 */

import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  Loader2,
  Lock,
  Trash2,
  X,
  Building2,
  GitBranch,
  Users,
  User,
  Monitor,
} from "lucide-react";

// ─── Tipos ────────────────────────────────────────────────────────────────────

type CascadeIcon = "branch" | "owner" | "manager" | "device" | "user";

export interface CascadeItem {
  label: string;
  count: number;
  icon: CascadeIcon;
}

interface DeleteWithAuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Llamado cuando el usuario confirma con contraseña válida.
   * El componente maneja el estado de carga (isDeleting).
   * Lanza un error si la autenticación o la eliminación falla.
   */
  onConfirm: (password: string) => Promise<void>;
  /** Tipo del recurso para mostrar en el título, ej: "empresa" */
  resourceType: string;
  /** Nombre del recurso específico, ej: "Pollos El Bronco" */
  resourceName: string;
  /** Lista de recursos dependientes que serán eliminados en cascada */
  cascadeItems: CascadeItem[];
  /** Si la operación de eliminación está en curso */
  isDeleting?: boolean;
}

// ─── Helper: icono por tipo ────────────────────────────────────────────────────

function CascadeIcon({ type }: { type: CascadeIcon }) {
  const cls = "h-4 w-4 flex-shrink-0";
  switch (type) {
    case "branch":  return <GitBranch className={cls} />;
    case "owner":   return <User className={cls} />;
    case "manager": return <Users className={cls} />;
    case "device":  return <Monitor className={cls} />;
    default:        return <Building2 className={cls} />;
  }
}

// ─── Componente ──────────────────────────────────────────────────────────────

export default function DeleteWithAuthDialog({
  isOpen,
  onClose,
  onConfirm,
  resourceType,
  resourceName,
  cascadeItems,
  isDeleting = false,
}: DeleteWithAuthDialogProps) {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Resetear al abrir
  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setIsLoading(false);
      // Foco diferido para que el dialog termine de montar
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  const busy = isLoading || isDeleting;

  const handleConfirm = async () => {
    if (!password.trim()) return;
    setIsLoading(true);
    try {
      await onConfirm(password);
      // onConfirm cierra el diálogo desde el padre si tiene éxito
    } catch {
      // El padre muestra el toast de error; aquí solo liberamos el loading
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  // Recursos con conteo > 0 (solo mostramos los afectados)
  const affectedItems = cascadeItems.filter((i) => i.count > 0);
  const hasAffected = affectedItems.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="sm:max-w-[460px] bg-slate-900 border-red-900/50 text-white"
        onInteractOutside={(e) => busy && e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <Trash2 className="h-5 w-5 text-red-400" />
            </div>
            <DialogTitle className="text-white text-lg font-semibold leading-tight">
              Eliminar {resourceType}
            </DialogTitle>
          </div>

          <DialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p className="text-white/70">
                Estás a punto de eliminar{" "}
                <span className="font-semibold text-white">"{resourceName}"</span>.
                Esta acción <span className="text-red-400 font-semibold">no se puede deshacer</span>.
              </p>

              {/* Aviso de cascada */}
              {hasAffected && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-red-400 font-semibold text-xs uppercase tracking-wider">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    Los siguientes recursos también serán eliminados:
                  </div>
                  <ul className="space-y-1.5 pl-1">
                    {affectedItems.map((item) => (
                      <li
                        key={item.label}
                        className="flex items-center gap-2 text-white/80 text-sm"
                      >
                        <CascadeIcon type={item.icon} />
                        <span>
                          <span className="font-semibold text-white">{item.count}</span>{" "}
                          {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!hasAffected && (
                <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 flex items-center gap-2 text-yellow-300 text-xs">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  No hay recursos dependientes. Se eliminará solo esta {resourceType}.
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Confirmación con contraseña */}
        <div className="space-y-2 pt-1">
          <Label
            htmlFor="delete-confirm-password"
            className="text-white/70 text-sm flex items-center gap-1.5"
          >
            <Lock className="h-3.5 w-3.5" />
            Confirma tu contraseña para continuar
          </Label>
          <Input
            id="delete-confirm-password"
            ref={inputRef}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Tu contraseña actual"
            className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                       focus:bg-white/10 focus:border-red-500/60"
            disabled={busy}
            onKeyDown={(e) => e.key === "Enter" && !busy && password.trim() && handleConfirm()}
            autoComplete="current-password"
          />
        </div>

        {/* Footer */}
        <DialogFooter className="flex flex-row justify-end gap-2 pt-1">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={busy}
            className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy || !password.trim()}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Eliminar definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
